import { commitSetSelectedArticle, commitSetWorkspaceViewArticles } from '@/store/commits/workspaceViewCommits';
import { dispatchOpenSelectedArticle } from '@/store/dispatchers/workspaceViewDispatchers';
import { ActionContext } from 'vuex';
import { State } from '@/store/state';
import confirmationService from '@/services/ConfirmationService';
import { DeletedItemType } from '@/@types/enums/ConfirmationDeleteItem/DeletedItemType';
import { DeletedItemStyleClass } from '@/@types/enums/ConfirmationDeleteItem/DeletedItemStyleClass';
import { Loading } from 'element-ui';
import apiService from '@/services/ApiService';
import { Article } from '@/models/article/Article';
import articleEditService from '@/services/article/ArticleEditService';
import urlService from '@/services/UrlService';
import navigationService from '@/services/NavigationService';
import { commitSetArticleMode } from '@/store/commits/sharedCommits';
import { ArticleEditMode } from '@/@types/ArticleEditMode';
import {
  dispatchGetDraftsCount,
  dispatchGetMeetingsCount,
  dispatchGetPersonalArticlesCount,
  dispatchGetRetentionCount,
  dispatchGetRetentionCountIfEnabled,
} from '@/store/dispatchers/uiDispatchers';
import workspaceService from '@/services/workspace/WorkspaceService';
import { RETENTION } from '@/routerNames';

export const articleActions = {
  async transferArticle(
    { state }: ActionContext<State, {}>,
    { article, companyId, workspaceId }: { article: Article; companyId: string; workspaceId: string }
  ) {
    const loader = Loading.service({ lock: true, text: 'The article is being transferred' });

    try {
      const res = await apiService.article.transfer(article.id, { companyId, workspaceId });

      const updatedOrder = workspaceService.removeArticleFromOrder(article.id, state.workspace.order);

      const success = !!res.data;

      if (success) {
        await apiService.workspace.updateArticlesOrder(state.workspace.id, updatedOrder);

        await dispatchGetDraftsCount();
        await dispatchGetPersonalArticlesCount();
        await dispatchGetRetentionCountIfEnabled();

        const articles = state.workspaceView.articles;
        const firstArticle = articles.find((a) => a.isArchived === false && a.id !== article.id);
        if (!firstArticle) return commitSetSelectedArticle(null);
        if (firstArticle) {
          commitSetArticleMode(firstArticle.isHasVideo ? ArticleEditMode.VIDEO : ArticleEditMode.NOTES);
          await dispatchOpenSelectedArticle({ articleId: firstArticle.id });
        }
      }
    } catch (err: any) {
      apiService.handleResponseError(err);
    } finally {
      loader.close();
    }
  },
  async deleteArticle({ state }: ActionContext<State, {}>, articleId: string) {
    const deleteArticleIds = articleEditService.findNestedArticles(state.workspace.order, articleId);
    if (!deleteArticleIds || !deleteArticleIds.length) return;

    const foundArticleItem = workspaceService.workspaceView.articles.find((a) => a.id === articleId);

    const result = await confirmationService
      .showRemovePopup(foundArticleItem.title, DeletedItemType.ARTICLE, DeletedItemStyleClass.ARTICLE)
      .catch(() => 'cancel');

    if (result !== 'confirm') return;

    const loader = Loading.service({ lock: true, text: 'The article is being deleted' });

    const promisesDelete = deleteArticleIds.map((aId) =>
      apiService.deleteArticle(aId).catch((err: any) => {
        // Ignore 404 — article may have been already removed (e.g. connector cleanup)
        if (err?.response?.status === 404) return;
        throw err;
      })
    );
    const updatedOrder = workspaceService.removeArticleFromOrder(articleId, state.workspace.order);
    try {
      await Promise.all(promisesDelete);
      await apiService.workspace.updateArticlesOrder(state.workspace.id, updatedOrder);

      const countPromises = [dispatchGetDraftsCount(), dispatchGetMeetingsCount(), dispatchGetPersonalArticlesCount()];

      if (state.company.retention.enabled) {
        countPromises.push(dispatchGetRetentionCount());
      }

      await Promise.all(countPromises);

      if (navigationService.isOpenedFrom(RETENTION)) {
        return navigationService.navigate({ name: RETENTION });
      }

      await navigationService.navigate(urlService.getWorkspaceViewRoute(state.workspace));

      if (state.workspaceView.articles) {
        // Refresh articles list to remove deleted articles from state
        const freshArticles = state.workspaceView.articles.filter((a: any) => !deleteArticleIds.includes(a.id));
        commitSetWorkspaceViewArticles(freshArticles);
        const firstArticleId = updatedOrder.length ? updatedOrder[0]?.value ?? null : freshArticles[0]?.id ?? null;
        const article = freshArticles.find((a: any) => a.id === firstArticleId);
        if (!article) return commitSetSelectedArticle(null);
        commitSetArticleMode(article.isHasVideo ? ArticleEditMode.VIDEO : ArticleEditMode.NOTES);
        await dispatchOpenSelectedArticle({ articleId: article.id });
      }
    } catch (err: any) {
      apiService.handleResponseError(err);
    } finally {
      loader.close();
    }
  },
  updateArticleMode({ state }: ActionContext<State, {}>, article: Article) {
    if (article.pinnedNote) {
      return commitSetArticleMode(ArticleEditMode.NOTES);
    }
    if (article.isVideoArticle() && state.articleMode !== ArticleEditMode.NOTES) {
      return commitSetArticleMode(ArticleEditMode.VIDEO);
    } else {
      return commitSetArticleMode(ArticleEditMode.NOTES);
    }
  },
};
